import { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

// Language context
export const LangContext = createContext({ lang: 'tr', setLang: () => {} });
export const useLang = () => useContext(LangContext);

const navItemsTR = [
  { name: 'Büyüme Sistemleri', href: '/buyume-sistemleri' },
  { name: 'aifabric', href: '/aifabric' },
  { name: 'Metodoloji', href: '/metodoloji' },
  { name: 'Portföy', href: '/portfolyo' },
];

const navItemsEN = [
  { name: 'Growth Systems', href: '/buyume-sistemleri' },
  { name: 'aifabric', href: '/aifabric' },
  { name: 'Methodology', href: '/metodoloji' },
  { name: 'Portfolio', href: '/portfolyo' },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState('tr');
  const location = useLocation();
  const navItems = lang === 'tr' ? navItemsTR : navItemsEN;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      {/* Desktop Navigation - Floating Island */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`nav-island hidden 2xl:flex flex-row items-center gap-1 ${
          isScrolled ? 'bg-black/90' : 'bg-black/50'
        }`}
        data-testid="main-navigation"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center mr-3 2xl:mr-5 flex-shrink-0" data-testid="nav-logo">
          <img 
            src="/images/brand/medyatik-logo-beyaz.png" 
            alt="Medyatik" 
            className="h-6 2xl:h-7 w-auto object-contain"
            style={{ minWidth: '100px' }}
          />
        </Link>

        {/* Nav Links */}
        <div className="flex flex-row items-center gap-0 flex-shrink-0">
          {navItems.map((item, index) => (
            <div key={item.name} className="flex items-center">
              {index > 0 && (
                <div className="w-[1px] h-4 bg-zinc-700/40 mx-3" />
              )}
              <Link
                to={item.href}
                className={`nav-link ${location.pathname === item.href ? 'active text-white' : ''}`}
                data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
            </div>
          ))}
        </div>

        {/* Lang Toggle */}
        <button
          onClick={() => setLang(lang === 'tr' ? 'en' : 'tr')}
          className="ml-2 px-2.5 py-1 rounded-full border border-zinc-700/50 bg-zinc-800/30 text-xs font-medium text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors flex-shrink-0"
          data-testid="lang-toggle"
        >
          {lang === 'tr' ? 'EN' : 'TR'}
        </button>

        {/* CTA Button */}
        <Link 
          to="/buyume-yolculugu"
          className="nav-cta-premium ml-3 2xl:ml-5 flex-shrink-0 whitespace-nowrap"
          data-testid="nav-cta-button"
        >
          <span className="nav-cta-shimmer" />
          <span className="relative z-10">{lang === 'tr' ? 'Büyüme Yolculuğunu Başlat' : 'Start Growth Journey'}</span>
        </Link>
      </motion.nav>

      {/* Mobile Navigation */}
      <div className="2xl:hidden fixed top-4 left-4 right-4 z-50 flex items-center justify-between">
        {/* Mobile Logo */}
        <Link to="/" className="glass rounded-full px-4 py-2" data-testid="mobile-nav-logo">
          <img 
            src="/images/brand/medyatik-logo-beyaz.png" 
            alt="Medyatik" 
            className="h-6 w-auto object-contain"
            style={{ minWidth: '100px' }}
          />
        </Link>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="glass rounded-full p-3"
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="mobile-menu 2xl:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col items-center gap-6">
              <Link 
                to="/"
                className="mobile-menu-link"
                data-testid="mobile-link-home"
              >
                Ana Sayfa
              </Link>
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`mobile-menu-link ${
                    location.pathname === item.href ? 'text-violet-400' : ''
                  }`}
                  data-testid={`mobile-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Lang Toggle */}
              <button
                onClick={() => setLang(lang === 'tr' ? 'en' : 'tr')}
                className="mt-6 px-4 py-2 rounded-full border border-zinc-700/50 bg-zinc-800/30 text-sm font-medium text-zinc-400 hover:text-white transition-colors self-center"
                data-testid="mobile-lang-toggle"
              >
                {lang === 'tr' ? 'English' : 'Türkçe'}
              </button>
              
              <Link 
                to="/buyume-yolculugu"
                className="mt-4 nav-cta-premium nav-cta-premium--mobile"
                data-testid="mobile-cta-button"
              >
                <span className="nav-cta-shimmer" />
                <span className="relative z-10">{lang === 'tr' ? 'Büyüme Yolculuğunu Başlat' : 'Start Growth Journey'}</span>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
